import React from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { initializeApollo } from '../utils/apollo';
import {
  Page,
  ComponentComponentsTechnologies,
  PagePageDynamicDynamicZone,
  Testimony,
  Technology,
  Event,
} from '../types/schema';
import { AvailableJobItemProps } from '../components/ui/AvailableJobItem';
import { flatten, translateProperty } from '../utils/object';
import { formatAvailableJobs } from '../utils/getAvailableJobsDevjs';
import CustomPage from '../components/elements/CustomPage';
import { CUSTOM_PAGE } from '../queries/pages';
import { gql } from '@apollo/client';
import PageLayout, { MenuProps } from '../layouts/Page';
import { PROJECT_PAGE } from '../queries/projects';
import { Project } from '../types/collections';
import { Error, sendError } from '../utils/error';

type CustomPageProps = {
  data: Page;
  technologies: ComponentComponentsTechnologies[];
  testimonies: Testimony[];
  events: Event[];
  locale: string;
  jobs: AvailableJobItemProps[];
  menus: MenuProps;
};

/**
 * Home
 */
const Home: NextPage<CustomPageProps> = ({
  data,
  technologies,
  testimonies,
  events,
  jobs,
  menus,
  locale,
}) => {
  const hasBanner = !!((data?.pageDynamic as PagePageDynamicDynamicZone[]) || []).find(
    (dz) => dz.__typename === 'ComponentComponentsBanner',
  );

  return (
    <PageLayout
      // hideMenu={!!data?.isLandingPage}
      menus={menus}
      locale={locale}
      isHeaderTransparent={hasBanner}
    >
      <CustomPage
        metaTitle={data?.metadata?.metaTitle as string}
        hideAppMastersTitle={data?.metadata?.hideAppMastersTitle as boolean}
        metaDescription={data?.metadata?.metaDescription as string}
        metaImage={(data?.metadata?.metaImage as unknown as { url?: string })?.url as string}
        technologies={technologies as unknown as Technology[]}
        testimonies={testimonies as Testimony[]}
        events={events as Event[]}
        pageDynamic={data?.pageDynamic as PagePageDynamicDynamicZone[]}
        jobs={jobs}
        language={locale}
      />
    </PageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query({
    query: gql`
      query GET_PAGES {
        pages(locale: "*") {
          slug
          locale
        }
      }
    `,
  });

  const newData = flatten(data);

  const paths = (newData as { pages: Page[] }).pages.map((page: Page) => {
    return {
      params: { slug: page.slug as string },
      locale: page.locale as string,
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

/**
 * getStaticProps
 */
export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale, params } = ctx;
  const apolloClient = initializeApollo();

  const slug = params?.slug as string;
  try {
    const isTeamBuilder = slug === 'team-builder';

    const { data } = await apolloClient.query({
      query: CUSTOM_PAGE,
      variables: {
        locale: isTeamBuilder ? '*' : locale,
        slug,
      },
    });

    const newData = flatten(data);

    // On TeamBuilder view, we don't have to worry about the locale
    if (
      !(newData as { pages: Page[] }).pages[0] ||
      (locale !== (newData as { pages: Page[] }).pages[0].locale && !isTeamBuilder)
    ) {
      return {
        notFound: true,
      };
    }

    const translatedObj = translateProperty(
      (newData as { pages: Page[] }).pages[0],
      [
        'title',
        'background',
        'galleryImagePosition',
        'limit',
        'textAlign',
        'button',
        'backgroundColor',
        'content',
      ],
      'pageDynamic',
    );

    if (
      (translatedObj.pageDynamic as PagePageDynamicDynamicZone[]).find(
        (p) => p.__typename === 'ComponentComponentsProjectsList',
      )
    ) {
      const { data } = await apolloClient.query({ query: PROJECT_PAGE, variables: { locale } });
      const newData = flatten(data);

      translatedObj.pageDynamic = (translatedObj.pageDynamic as PagePageDynamicDynamicZone[]).map(
        (p) => {
          if (p.__typename === 'ComponentComponentsProjectsList') {
            const projects = newData.allProjects as Project[];
            return {
              ...p,
              projects: projects
                .filter(({ featured }) => (p.filter && p.filter === 'featured' ? featured : true))
                .slice(0, p.limit || projects.length),
            };
          }

          return p;
        },
      );
    }

    const formatedAvailableJobs = await formatAvailableJobs();
    return {
      props: {
        data: translatedObj,
        technologies: newData.technologiesData,
        testimonies: newData.testimonies,
        events: newData.eventsData,
        locale,
        jobs: formatedAvailableJobs,
        menus: {
          header: newData.header,
          technologies: newData.technologies,
          services: newData.services,
          footerPt: newData.footerPt,
          footerEn: newData.footerEn,
        },
      },
    };
  } catch (error) {
    sendError({ error: error as Error, slug, message: 'Error on [slug] pages' });
    return {
      notFound: true,
    };
  }
};

export default Home;
